import { useState, useRef, useCallback, useEffect } from 'react';
import { useArt } from '../../../backend/useArt';
import { useArt3D } from '../../../backend/useArt3D';
import { ArtType } from '../Art';

interface ArtNameProps {
  artType: ArtType;
  artId: string;
  initialName?: string;
  description: string;
  hasAIImage: boolean;
}

/**
 * Hook for managing art name
 */
export const useArtName = ({ artType, artId, initialName = '...', description = '', hasAIImage = false }: ArtNameProps) => {
  const { updateArt } = useArt();
  const { updateArt3D } = useArt3D();
  
  // State
  const [artName, setArtName] = useState(initialName);
  
  // Refs for debouncing
  const artNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Use description for art name if name is default
  useEffect(() => {
    // Only proceed if we have a description and the art name is the default
    if (description && artName === 'New Art Project' && hasAIImage) {
      // Take first 20 characters of description (or less if description is shorter)
      const nameFromDescription = description.substring(0, 20) + (description.length > 20 ? '...' : '');
      
      // Update the art name both locally and in the database
      setArtName(nameFromDescription);
      
      // Update in database
      if (artType === '3D') {
        updateArt3D(artId, {
          art_name: nameFromDescription
        }).catch(error => {
          console.error('Error updating 3D art name from description:', error);
        });
      } else {
        updateArt(artId, {
          art_name: nameFromDescription
        }).catch(error => {
          console.error('Error updating art name from description:', error);
        });
      }
    }
  }, [description, artName, artId, updateArt, updateArt3D, artType, hasAIImage]);
  
  /**
   * Update the art name with debounce and save to database
   */
  const handleArtNameChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setArtName(value);
    
    // Clear any existing timeout
    if (artNameTimeoutRef.current) {
      clearTimeout(artNameTimeoutRef.current);
    }
    
    // Set a new timeout - only update the art name in the database after user stops typing
    artNameTimeoutRef.current = setTimeout(async () => {
      try {
        if (artType === '3D') {
          await updateArt3D(artId, {
            art_name: value
          });
        } else {
          await updateArt(artId, {
            art_name: value
          });
        }
      } catch (error) {
        console.error('Error updating art name:', error);
      }
    }, 1000); // 1 sec delay after user stops typing
  }, [artId, updateArt]);
  
  /**
   * Update the art name without user input (e.g. when loading from props)
   */
  const updateArtName = useCallback((name: string) => {
    setArtName(name);
  }, []);
  
  return {
    artName,
    handleArtNameChange,
    updateArtName,
  };
};